import React, { useState, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bot, X, Plus, Sparkles, Loader2, Mic, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const aiFieldsConfig = [
  { key: 'key_points', label: 'Puntos Clave', placeholder: 'Puntos más importantes de la actividad...' },
  { key: 'decisions', label: 'Decisiones', placeholder: 'Decisiones tomadas...' },
  { key: 'next_steps', label: 'Próximos Pasos', placeholder: 'Acciones a realizar...' },
  { key: 'risks_concerns', label: 'Riesgos', placeholder: 'Riesgos o preocupaciones identificados...' },
  { key: 'opportunity_signals', label: 'Oportunidades', placeholder: 'Oportunidades detectadas...' },
];

const ActivityAIFields = ({ 
  formData, 
  onChange,
  showTranscript = true,
  isFromVoiceNote = false,
  currentUser = null
}) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Check if any AI field already has data (to disable the AI button)
  const hasAnyAIFieldData = aiFieldsConfig.some(f => formData[f.key]?.length > 0);

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setRecording(true);
      toast.info('Grabando... Pulsa de nuevo para detener');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('No se pudo acceder al micrófono');
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  // Transcribe audio to text
  const transcribeAudio = async (audioBlob) => {
    setTranscribing(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        
        try {
          const response = await axios.post(
            `${API}/utils/transcribe-base64`,
            { audio_data: base64Audio },
            { headers: { 'x-user-email': currentUser?.email || '' } }
          );

          const transcribedText = response.data.text || '';
          
          if (transcribedText) {
            // Append to existing transcript
            const currentTranscript = formData.transcript || '';
            const newTranscript = currentTranscript 
              ? `${currentTranscript}\n\n${transcribedText}`
              : transcribedText;
            onChange('transcript', newTranscript);
            toast.success('Audio transcrito correctamente');
          } else {
            toast.warning('No se detectó texto en el audio');
          }
        } catch (error) {
          console.error('Error transcribing:', error);
          toast.error('Error al transcribir el audio');
        } finally {
          setTranscribing(false);
        }
      };
    } catch (error) {
      console.error('Error processing audio:', error);
      toast.error('Error al procesar el audio');
      setTranscribing(false);
    }
  };

  // Handle AI analysis of transcript
  const handleAnalyzeWithAI = async () => {
    if (!formData.transcript || formData.transcript.trim().length < 10) {
      toast.error('Escribe al menos algunas notas para analizar');
      return;
    }

    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/activities/analyze-notes`,
        { transcript: formData.transcript },
        { headers: { 'x-user-email': currentUser?.email || '' } }
      );

      // Update all fields with AI response
      const { key_points, decisions, next_steps, risks_concerns, opportunity_signals } = response.data;
      
      if (key_points?.length) onChange('key_points', key_points);
      if (decisions?.length) onChange('decisions', decisions);
      if (next_steps?.length) onChange('next_steps', next_steps);
      if (risks_concerns?.length) onChange('risks_concerns', risks_concerns);
      if (opportunity_signals?.length) onChange('opportunity_signals', opportunity_signals);

      toast.success('Notas analizadas correctamente');
    } catch (error) {
      console.error('Error analyzing notes:', error);
      toast.error(error.response?.data?.detail || 'Error al analizar las notas');
    } finally {
      setAnalyzing(false);
    }
  };
  // Helper to update array fields
  const updateArrayField = (field, index, value) => {
    const currentArray = formData[field] || [];
    const newArray = [...currentArray];
    newArray[index] = value;
    onChange(field, newArray);
  };

  const addArrayItem = (field) => {
    const currentArray = formData[field] || [];
    onChange(field, [...currentArray, '']);
  };

  const removeArrayItem = (field, index) => {
    const currentArray = formData[field] || [];
    onChange(field, currentArray.filter((_, i) => i !== index));
  };

  // Check if any AI field has data
  const hasAIData = aiFieldsConfig.some(f => formData[f.key]?.length > 0) || formData.transcript;

  return (
    <Card className="border-blue-200 bg-blue-50/30 dark:bg-blue-900/10">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-blue-600" />
          <CardTitle className="text-sm font-medium">
            Datos de la Actividad
            {isFromVoiceNote && (
              <Badge variant="outline" className="ml-2 text-xs">Desde nota de voz</Badge>
            )}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {aiFieldsConfig.map(({ key, label, placeholder }) => {
          const items = formData[key] || [];
          
          return (
            <div key={key} className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">{label}</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => addArrayItem(key)}
                  className="h-6 px-2"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Añadir
                </Button>
              </div>
              
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">Sin {label.toLowerCase()}</p>
              ) : (
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <span className="text-xs text-muted-foreground mt-2 w-4">{index + 1}.</span>
                      <Input
                        value={item}
                        onChange={(e) => updateArrayField(key, index, e.target.value)}
                        placeholder={placeholder}
                        className="flex-1 text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeArrayItem(key, index)}
                        className="h-8 px-2 text-muted-foreground hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Transcript */}
        {showTranscript && (
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-medium">Transcripción / Notas detalladas</Label>
              <div className="flex items-center gap-1">
                {/* Voice Recording Button */}
                <Button
                  type="button"
                  variant={recording ? "destructive" : "outline"}
                  size="sm"
                  onClick={recording ? stopRecording : startRecording}
                  disabled={transcribing}
                  className="h-7 px-2 gap-1 text-xs"
                  title={recording ? 'Detener grabación' : 'Grabar voz'}
                >
                  {transcribing ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Transcribiendo...
                    </>
                  ) : recording ? (
                    <>
                      <Square className="w-3 h-3" />
                      Detener
                    </>
                  ) : (
                    <>
                      <Mic className="w-3 h-3" />
                      Grabar
                    </>
                  )}
                </Button>
                
                {/* AI Analysis Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAnalyzeWithAI}
                  disabled={analyzing || hasAnyAIFieldData || !formData.transcript?.trim()}
                  className="h-7 px-2 gap-1 text-xs"
                  title={hasAnyAIFieldData ? 'Los campos ya tienen datos' : 'Analizar notas con IA'}
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Analizando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3" />
                      Analizar con IA
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Recording indicator */}
            {recording && (
              <div className="flex items-center gap-2 text-xs text-red-600 animate-pulse">
                <div className="w-2 h-2 bg-red-600 rounded-full" />
                Grabando... Pulsa "Detener" cuando termines
              </div>
            )}
            
            <Textarea
              value={formData.transcript || ''}
              onChange={(e) => onChange('transcript', e.target.value)}
              placeholder="Transcripción del audio o notas detalladas..."
              className="min-h-[100px] text-sm"
            />
            {hasAnyAIFieldData && (
              <p className="text-xs text-muted-foreground">
                El botón de IA está deshabilitado porque ya hay datos en los campos de análisis.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityAIFields;
